import { useState, type FC } from 'react'

import { LoaderCircle, Tooltip } from '@skytech/ui'
import { Image } from '@skytech/ui/Image'
import InformationCircleIcon from 'mdi-react/InformationCircleIcon'
import UnfoldMoreHorizontalIcon from 'mdi-react/UnfoldMoreHorizontalIcon'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Highlight from '@/components/Highlight'
import { parseInStock } from '@/modules/ecom-policy/helpers'
import { imagePlaceholder } from '@/util/misc'

import { usePolicyId, useUpdateProduct } from '../hooks'
import { usePolicyFilters } from '../Policy.context'

import { ProductSettingsPopup } from './ProductSettings'

import type { ProductExtended } from '../../types'

import './Product.styles.scss'

const Product: FC<
  Pick<
    ProductExtended,
    | 'id'
    | 'name'
    | 'priceId'
    | 'imgUrl'
    | 'price'
    | 'inStock'
    | 'vatPct'
    | 'isSelected'
    | 'parentProductIdList'
    | 'isExpired'
    | 'isMandatory'
    | 'quantityLimit'
    | 'alias'
    | 'currency'
  > & { hasAccessories: boolean }
> = ({
  id,
  name,
  priceId,
  imgUrl,
  price,
  inStock,
  vatPct,
  alias,
  isSelected,
  isMandatory,
  isExpired,
  currency,
  quantityLimit,
  hasAccessories,
}) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()
  const { updateProduct, isUpdating } = useUpdateProduct()
  const policyId = usePolicyId()
  const filters = usePolicyFilters()

  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  const [searchQuery] = filters.searchQuery

  const isProductExpired = Boolean(isExpired && isSelected)
  const [isVatIncluded] = filters.isVatIncluded

  const handleProductClick = () => {
    if (isSelected) {
      setIsSettingsPopupOpen(true)
      return
    }

    updateProduct({
      policyId,
      payload: {
        products: [
          {
            productId: id,
            isSelected: !isSelected,
            priceId,
            isMandatory: Boolean(isMandatory),
            quantityLimit,
            alias,
          },
        ],
      },
    })
  }

  return (
    <button
      className={`ecom-policy__products__product ${whiteLabel} ${isUpdating ? 'is-saving' : ''} ${
        isSelected ? 'is-selected' : ''
      } ${isProductExpired ? 'is-expired' : ''} ${hasAccessories ? 'has-accessories' : ''} ${
        isMandatory ? 'is-mandatory' : ''
      }`}
      disabled={isUpdating}
      onClick={handleProductClick}
    >
      {(isProductExpired || Boolean(isSelected)) && (
        <div className="status-icons">
          {Boolean(isSelected) && (
            <>
              {quantityLimit > 1 && (
                <div className="mt-1" id={`quantity-selector-enabled-${id}`}>
                  <UnfoldMoreHorizontalIcon />

                  <Tooltip
                    anchorId={`quantity-selector-enabled-${id}`}
                    content={translate('Quantity selector enabled')}
                  />
                </div>
              )}
            </>
          )}

          {isProductExpired && (
            <div className="expired-info" id="expired-info">
              <InformationCircleIcon />

              <Tooltip
                anchorId="expired-info"
                content={translate('Produktet er utgått hos leverandør')}
              />
            </div>
          )}
        </div>
      )}

      <div>
        <div
          className={`ecom-policy__products__product__name ${isProductExpired ? 'is-expired' : ''}`}
        >
          <Highlight pattern={searchQuery} text={alias || name} />
        </div>
      </div>

      <Image src={imgUrl || 'missing'} alt="product" onError={imagePlaceholder} />

      <div>
        <div className="ecom-policy__products__product__price">
          <FormattedNumberWithCurrency
            amount={isVatIncluded ? price * (1 + vatPct / 100) : price}
            currency={currency}
          />
        </div>

        <div className="ecom-policy__products__product__stock">
          {parseInStock(inStock)} <Translation id="in stock" />
        </div>
      </div>

      {isUpdating && (
        <div className="ecom-policy__products__product__loader">
          <LoaderCircle />
        </div>
      )}

      <ProductSettingsPopup
        alias={alias}
        isMandatory={Boolean(isMandatory)}
        isOpen={isSettingsPopupOpen}
        name={name}
        onClose={() => setIsSettingsPopupOpen(false)}
        policyId={policyId}
        priceId={priceId}
        productId={id}
        quantityLimit={quantityLimit}
      />
    </button>
  )
}

export default Product
